/**
 * @module services/authorization
 * @description Servicio HTTP para el módulo de autorizaciones de prestaciones.
 * Consume los endpoints de /authorization-cases del backend.
 */

import api from "./api";

const authorizationService = {
  /**
   * @description Crea un nuevo caso de autorización.
   * @param {Object} data - { origin, memberId, providerId, practiceId, observations, requestedAt }
   * @returns {Promise<Object>} Caso creado
   */
  create(data) {
    return api.post("/authorization-cases", data).then((r) => r.data);
  },

  /**
   * @description Lista casos con filtros y paginación.
   * @param {Object} params - { status, origin, memberId, providerId, searchInput, offset, limit }
   * @returns {Promise<{total: number, docs: Array}>}
   */
  get(params = {}) {
    return api.get("/authorization-cases", { params }).then((r) => r.data);
  },

  /**
   * @description Obtiene el detalle completo de un caso.
   * @param {string} id - ID del caso
   * @returns {Promise<Object>}
   */
  getById(id) {
    return api.get(`/authorization-cases/${id}`).then((r) => r.data);
  },

  /**
   * @description Actualiza observaciones de un caso pendiente.
   * @param {string} id
   * @param {Object} data - { observations }
   * @returns {Promise<Object>}
   */
  update(id, data) {
    return api.put(`/authorization-cases/${id}`, data).then((r) => r.data);
  },

  /**
   * @description Consulta una autorización por código AUTH sin ejecutarla.
   * Devuelve datos del caso, afiliado, prestación y verificación de contrato.
   * @param {string} code - Código AUTH (ej: AUTH-2026-0000000001)
   * @returns {Promise<Object>} Datos de la autorización con canExecute y contractWarning
   */
  lookup(code) {
    return api
      .get("/authorization-cases/lookup", { params: { code } })
      .then((r) => r.data);
  },

  /**
   * @description Resuelve un caso: aprueba, rechaza o deriva a auditoría.
   * @param {string} id
   * @param {Object} data - { status, rejectionReason, auditNotes, approvalScope, approvedProviderId, approvedFrom, approvedUntil }
   * @returns {Promise<Object>}
   */
  resolve(id, data) {
    return api
      .put(`/authorization-cases/${id}/resolve`, data)
      .then((r) => r.data);
  },

  /**
   * @description Sube archivos adjuntos al caso (órdenes, estudios).
   * @param {string} id
   * @param {FormData} formData
   * @returns {Promise<Object>}
   */
  uploadFiles(id, formData) {
    return api
      .put(`/authorization-cases/${id}/files`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data);
  },

  /**
   * @description Obtiene la URL temporal firmada para descargar un adjunto.
   * @param {string} id - ID del caso
   * @param {string} key - Key S3 del archivo (URL-encoded)
   * @returns {Promise<{url: string}>}
   */
  getFileUrl(id, key) {
    return api
      .get(`/authorization-cases/${id}/files/${encodeURIComponent(key)}`)
      .then((r) => r.data);
  },

  /**
   * @description Lista prestadores con contrato activo para una prestación específica.
   * Usado al aprobar con scope=specific_provider.
   * @param {string} practiceId
   * @returns {Promise<Array>}
   */
  getProvidersForPractice(practiceId) {
    return api
      .get("/authorization-cases/providers-for-practice", {
        params: { practiceId },
      })
      .then((r) => r.data);
  },

  /**
   * @description Lista configuraciones de vigencia.
   * @returns {Promise<Array>}
   */
  getValidityConfigs() {
    return api.get("/authorization-cases/validity-config").then((r) => r.data);
  },

  /**
   * @description Crea una configuración de vigencia.
   * @param {Object} data - { practiceCategory, defaultDays, requiresAudit, notes }
   * @returns {Promise<Object>}
   */
  createValidityConfig(data) {
    return api
      .post("/authorization-cases/validity-config", data)
      .then((r) => r.data);
  },

  /**
   * @description Actualiza una configuración de vigencia.
   * @param {string} id
   * @param {Object} data
   * @returns {Promise<Object>}
   */
  updateValidityConfig(id, data) {
    return api
      .put(`/authorization-cases/validity-config/${id}`, data)
      .then((r) => r.data);
  },

  /**
   * @description Elimina una configuración de vigencia.
   * @param {string} id
   * @returns {Promise<void>}
   */
  removeValidityConfig(id) {
    return api
      .delete(`/authorization-cases/validity-config/${id}`)
      .then((r) => r.data);
  },
};

export default authorizationService;
